import { useState } from 'react';
import { ATTRIBUTE_MAX, ATTRIBUTE_MIN, countAttributePointsSpent, isSameAttributeAllocation, MINIMUM_CHARACTER_ATTRIBUTES } from '@sorrem/shared';
import type { UIPartyCharacter } from '@sorrem-client/types';
import { Button, CharacterAttributes, Modal, Panel } from '@sorrem-client/ui/components';
import { useAttributeRespec } from '@sorrem-client/ui/hooks';
import styles from './AttributeRespecModal.module.scss';

interface AttributeRespecModalProps {
    character: UIPartyCharacter;
    onClose: () => void;
}

/** A character's attribute allocation (named apart from the `CharacterAttributes` component). */
type AttributeAllocation = UIPartyCharacter['attributes'];

/**
 * Resets and reallocates a character's attributes for banked essence (#97).
 * Starts from their current attributes; every earned point must be spent,
 * and the allocation has to change before it can be confirmed.
 */
export function AttributeRespecModal({ character, onClose }: AttributeRespecModalProps): JSX.Element {
    const { cost, bankedValue, earnedPoints, isRespeccing, error, respec } = useAttributeRespec(character);
    const [draft, setDraft] = useState<AttributeAllocation>({ ...character.attributes });

    const remainingPoints = earnedPoints - countAttributePointsSpent(draft);
    const isChanged = !isSameAttributeAllocation(draft, character.attributes);
    const canAfford = bankedValue >= cost;
    const canConfirm = isChanged && remainingPoints === 0 && canAfford && !isRespeccing;

    const handleChange = (attribute: keyof AttributeAllocation, nextValue: number): void => {
        setDraft((current) => ({ ...current, [attribute]: nextValue }));
    };

    const handleConfirm = async (): Promise<void> => {
        if (await respec(draft)) {
            onClose();
        }
    };

    return (
        <Modal label="Reshape attributes" onClose={onClose}>
            <Panel className={styles.respec} frame="thick">
                <h3 className={styles.title}>Reshape attributes</h3>
                <p className={styles.note}>
                    As one of the Veil-Touched, your attributes have been enhanced by the Veil.
                </p>
                <p className={styles.note}>
                    You may redistribute these enhancements to other attributes by spending Essence. However, the more often you alter your gifts, and the longer you have lived with them, the more difficult and costly it becomes.
                </p>

                <CharacterAttributes
                    attributes={draft}
                    mode="edit"
                    remainingPoints={remainingPoints}
                    minValue={ATTRIBUTE_MIN}
                    maxValue={ATTRIBUTE_MAX}
                    isDisabled={isRespeccing}
                    isNoteShown={false}
                    onChangeAttribute={handleChange}
                />

                <p className={canAfford ? styles.balance : styles.shortfall}>
                    {canAfford
                        ? `Your vessel holds ${bankedValue} essence.`
                        : `Your vessel holds ${bankedValue} essence; you need ${cost}.`}
                </p>
                {error && <p className={styles.error}>{error}</p>}

                <div className={styles.actions}>
                    <Button variant="secondary" onClick={() => setDraft({ ...MINIMUM_CHARACTER_ATTRIBUTES })} disabled={isRespeccing}>
                        Clear all
                    </Button>
                    <Button variant="secondary" onClick={onClose} disabled={isRespeccing}>
                        Cancel
                    </Button>
                    <Button onClick={() => void handleConfirm()} disabled={!canConfirm}>
                        Reshape for {cost} essence
                    </Button>
                </div>
            </Panel>
        </Modal>
    );
}
